import { AssessmentType, UserProfileRole } from '@@types/api.types';
import { AssessmentHistoryChartComponent } from '@components/shared/assessment-history-chart.component';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { useDraftSurveyState } from '@hooks/surveys.hook';
import { useGetAssessmentHistory } from '@queries/technology.query';
import { useGetTechnology } from '@queries/technology.query';
import { useGetCurrentUser } from '@queries/users.query';
import { isChildOrganizationView as getIsChildOrganizationView } from '@utils/parent-organization.util';
import { FunctionComponent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { StartTRLModal } from './technology-start-rapid-trl-modal.component';

type TechnologyAssessmentHistoryProps = {
  fixedType?: AssessmentType;
  onDataPointClick?: (assessmentId: string | null) => void;
  title?: string;
};

export const TechnologyAssessmentHistory: FunctionComponent<TechnologyAssessmentHistoryProps> = (props) => {
  const { fixedType, onDataPointClick, title } = props;
  const [trlModalOpen, setTRLModalOpen] = useState(false);
  const [assessmentType, setAssessmentType] = useState<AssessmentType>(fixedType ?? AssessmentType.TRL);

  const { technologyId } = useParams();
  const { data: technology, isPending: isTechnologyLoading } = useGetTechnology(technologyId);
  const { data: user, isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();
  const { emptyStateMessages, isDraftSurveysPending, handleResumeSurvey, hasDraftAssessment } =
    useDraftSurveyState(assessmentType);

  const isChildOrganizationView = getIsChildOrganizationView({
    isSelectedCompanyParentOrganization,
    selectedCompanyId: selectedCompany?.id,
    technologyCompanyId: technology?.company?.id,
  });
  const canEdit = user?.profile?.role === UserProfileRole.INNOVATOR && !isChildOrganizationView;

  const { data: historyData, isPending: isHistoryLoading } = useGetAssessmentHistory(technologyId);

  const hasTrlGroupedTimeline = Boolean(historyData?.trlTimelineByUseDomain);
  const isEmptyTrlTimeline = hasTrlGroupedTimeline
    ? historyData?.trlTimelineByUseDomain?.length === 0
    : historyData?.trlTimeline?.length === 0 || !historyData?.trlTimeline;
  const isEmptyMrlTimeline = historyData?.mrlTimeline?.length === 0 || !historyData?.mrlTimeline;

  const isCurrentTypeEmpty =
    (assessmentType === AssessmentType.TRL && isEmptyTrlTimeline) ||
    (assessmentType === AssessmentType.MRL && isEmptyMrlTimeline);

  const isEmpty = !historyData || (fixedType ? isCurrentTypeEmpty : isEmptyTrlTimeline && isEmptyMrlTimeline);

  if (isTechnologyLoading) return null;

  const handleEmptyStateClick = () => {
    if (hasDraftAssessment) {
      handleResumeSurvey();
    } else if (assessmentType === AssessmentType.TRL) {
      setTRLModalOpen(true);
    }
  };

  const displayTitle = title ?? `${assessmentType === AssessmentType.TRL ? 'TRL' : 'MRL'} Timeline`;

  return (
    <>
      <Tile
        emptyState={{
          message: canEdit ? emptyStateMessages.message : undefined,
          onClick: canEdit ? handleEmptyStateClick : undefined,
          title: emptyStateMessages.title,
        }}
        header={{
          title: displayTitle,
          actions: fixedType ? undefined : (
            <div className="flex gap-1 ml-auto font-normal">
              <Button
                variant={assessmentType === AssessmentType.TRL ? 'active' : 'secondary'}
                onClick={() => setAssessmentType(AssessmentType.TRL)}
                size="sm"
              >
                TRL
              </Button>
              <Button
                variant={assessmentType === AssessmentType.MRL ? 'active' : 'secondary'}
                onClick={() => setAssessmentType(AssessmentType.MRL)}
                size="sm"
              >
                MRL
              </Button>
            </div>
          ),
        }}
        isEmpty={isEmpty}
        isLoading={isTechnologyLoading || isDraftSurveysPending || isHistoryLoading}
      >
        <AssessmentHistoryChartComponent
          assessmentType={assessmentType}
          historyData={historyData}
          onDataPointClick={onDataPointClick}
        />
      </Tile>
      {assessmentType === AssessmentType.TRL && (
        <StartTRLModal
          isOpen={trlModalOpen}
          onClose={() => setTRLModalOpen(false)}
          technology={technology}
        />
      )}
    </>
  );
};
